/**
 * Picks API — three tea-time options (eat-out / takeaway / cook-in).
 *
 * `getTodayPicks` calls `GET /v1/picks/teatime` on towninsider-api. The
 * endpoint is requireUser-gated: it resolves the user's town from the
 * authenticated session and returns exactly three picks — one eat_out,
 * one takeaway, one cook_in — for the given local date. No town or
 * profile is sent; the server derives everything from the session.
 *
 * The pre-API mock (hardcoded MOCK_PICKS + client-side "Dog welcome."
 * annotation) has been removed. v1 of the endpoint does not annotate
 * blurbs — the catalogue blurb is returned verbatim. If per-profile
 * annotation returns later, it lands server-side.
 *
 * Drift safety net: `teatimePicksResponseSchema` is validated inside
 * `apiFetch`, and exercised against the live API on every push/PR by
 * `tests/integration/teatime.integration.test.ts`.
 */
import { useAuth } from '@clerk/clerk-react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch } from '../api';

// ----- Schemas / types -----

export const pickCategorySchema = z.enum(['eat_out', 'takeaway', 'cook_in']);
export type PickCategory = z.infer<typeof pickCategorySchema>;

export const pickSchema = z.object({
  id: z.string().min(1),
  category: pickCategorySchema,
  name: z.string().min(1),
  /** Description from the catalogue. */
  blurb: z.string().min(1),
});
export type Pick = z.infer<typeof pickSchema>;

export const teatimePicksResponseSchema = z.object({
  /** Local YYYY-MM-DD the picks are for — echoed by the server. */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  picks: z.array(pickSchema).length(3),
});
export type TeatimePicksResponse = z.infer<typeof teatimePicksResponseSchema>;

// ----- API call -----

export interface GetTodayPicksInput {
  /** YYYY-MM-DD (local). Sent as `?date=` and used in the cache key. */
  readonly date: string;
  /** Clerk-aware token getter — `GET /v1/picks/teatime` is auth-gated. */
  readonly getToken: () => Promise<string | null>;
  readonly signal?: AbortSignal;
}

/**
 * Fetch tea-time picks for a given local date.
 *
 * `GET /v1/picks/teatime?date=YYYY-MM-DD`. The town is resolved from the
 * authenticated session server-side, so it is NOT a query param. Returns
 * exactly three picks (one per category); the response is Zod-validated
 * against `teatimePicksResponseSchema` inside `apiFetch`.
 *
 * Throws `ApiError` on:
 *   - 401 'unauthorized'        — missing / invalid token
 *   - 403 'onboarding_required' — authed but no users row
 *   - 503 'server_error'        — the town's catalogue can't fill all
 *                                 three slots
 *   - 0   'network_error'       — connectivity failure
 *   - 0   'validation_error'    — response shape drifted from the schema
 */
export async function getTodayPicks(
  input: GetTodayPicksInput,
): Promise<TeatimePicksResponse> {
  return apiFetch(
    `/v1/picks/teatime?date=${encodeURIComponent(input.date)}`,
    {
      method: 'GET',
      schema: teatimePicksResponseSchema,
      getToken: input.getToken,
      ...(input.signal !== undefined ? { signal: input.signal } : {}),
    },
  );
}

// ----- Hook -----

/**
 * Declarative variant of `getTodayPicks`. MePage's tea-time nudge
 * fetches imperatively via `queryClient.fetchQuery` (it is event-driven,
 * fired by the 16:00 trigger, not render-driven) — this hook exists for
 * any render-driven caller. The token getter is sourced from Clerk
 * internally; `townSlug` is used only for cache-key namespacing.
 */
export function useTodayPicks(input: {
  townSlug: string;
  date: string;
  enabled?: boolean;
}): UseQueryResult<TeatimePicksResponse, Error> {
  const { getToken } = useAuth();
  return useQuery<TeatimePicksResponse, Error>({
    queryKey: teatimePicksQueryKey(input.townSlug, input.date),
    queryFn: () => getTodayPicks({ date: input.date, getToken }),
    // Picks are valid for the evening — don't refetch if the user just
    // opens the app twice in an hour.
    staleTime: 4 * 60 * 60 * 1000,
    enabled: input.enabled ?? true,
  });
}

/** Shared query key — for imperative `queryClient.fetchQuery` use. */
export function teatimePicksQueryKey(
  townSlug: string,
  date: string,
): readonly unknown[] {
  return ['teatime-picks', townSlug.toLowerCase(), date];
}

// ----- Pick-name matching (for venue-selection intent detection) -----

/**
 * Suffix words to TRIM off the end of a pick name when generating a stem
 * key (e.g. "Pickings deli" → "Pickings"). These are removed from the end
 * of the name to expose a cleaner core for matching.
 */
const VENUE_SUFFIXES: ReadonlySet<string> = new Set([
  'deli',
  'co',
  'co.',
  'chippy',
  'bar',
  'inn',
  'restaurant',
  'pub',
  'kitchen',
  'cafe',
  'café',
  'shop',
  'house',
  'lodge',
  'cellars',
  'bakery',
]);

/**
 * Subset of VENUE_SUFFIXES distinctive enough to also serve as a standalone
 * match key. "Chippy" → Cherry Trees Chippy is fine; "deli" → Pickings deli
 * is too generic (would false-positive on "deli sandwich"). Same with
 * "house", "shop", "lodge".
 */
const DISTINCTIVE_SUFFIXES: ReadonlySet<string> = new Set([
  'chippy',
  'bakery',
  'cellars',
]);

const LEADING_ARTICLES: ReadonlySet<string> = new Set(['the', 'a', 'an']);
const MIN_KEY_LEN = 4;

function stripApostrophes(s: string): string {
  return s.replace(/['\u2019]/g, '');
}

function addKey(set: Set<string>, key: string): void {
  const trimmed = key.trim();
  if (trimmed.length < MIN_KEY_LEN) return;
  set.add(trimmed);
  const stripped = stripApostrophes(trimmed);
  if (stripped !== trimmed && stripped.length >= MIN_KEY_LEN) {
    set.add(stripped);
  }
}

/**
 * Generate a list of lowercase keys for matching a Pick's name in user text.
 *
 * For "Cherry Trees Chippy" returns: ["cherry trees chippy", "cherry trees", "cherry", "chippy"]
 * For "Rojano's at the Square" returns: ["rojano's at the square", "rojanos at the square", "rojano's", "rojanos", "rojano"]
 * For "The Seafood Bar" returns: ["the seafood bar", "seafood bar", "seafood"]
 * For "Pickings deli" returns: ["pickings deli", "pickings", "deli"]
 *
 * Keys shorter than 4 chars are excluded to avoid false-positives on
 * common short words like "the", "co", "bar", "inn".
 */
export function pickMatchKeys(pick: Pick): ReadonlyArray<string> {
  const name = pick.name.toLowerCase().trim();
  const tokens = name
    .split(/\s+/)
    .map((t) => t.replace(/[.,]+$/, ''))
    .filter((t) => t.length > 0);

  const keys = new Set<string>();

  // Full normalised name
  addKey(keys, name);

  // Drop leading article ("The Seafood Bar" → "seafood bar")
  let working = tokens;
  const head = tokens[0];
  if (tokens.length > 1 && head !== undefined && LEADING_ARTICLES.has(head)) {
    working = tokens.slice(1);
    addKey(keys, working.join(' '));
  }

  // Drop trailing venue suffix ("seafood bar" → "seafood", "Pickings deli" → "Pickings")
  if (working.length > 1) {
    const tail = working[working.length - 1];
    if (tail !== undefined && VENUE_SUFFIXES.has(tail)) {
      const stem = working.slice(0, -1).join(' ');
      addKey(keys, stem);
      // Also expose the suffix word itself as a standalone match key, but
      // ONLY if it's distinctive (chippy, bakery). Generic suffixes (deli,
      // house, shop) are skipped to avoid false positives on common words.
      if (DISTINCTIVE_SUFFIXES.has(tail) && tail.length >= MIN_KEY_LEN) {
        addKey(keys, tail);
      }
    }
  }

  // First distinctive token + 's possessive variants
  const first = working[0];
  if (first !== undefined && first.length >= MIN_KEY_LEN) {
    addKey(keys, first);
    const noPossessive = first.replace(/['\u2019]s$/, '');
    if (noPossessive !== first) {
      addKey(keys, noPossessive);
    }
  }

  return Array.from(keys);
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Detect whether the user's text mentions one of the given picks. Returns
 * the matched Pick or null. Uses word-boundary matching so "rojano" matches
 * but "europinion" does not.
 *
 * Pure function, no async — callers must supply the picks (typically read
 * from TanStack Query cache so we don't refetch on every keystroke).
 */
export function detectPickSelection(
  text: string,
  picks: ReadonlyArray<Pick>,
): Pick | null {
  if (text.trim().length === 0) return null;
  for (const pick of picks) {
    const keys = pickMatchKeys(pick);
    for (const key of keys) {
      const pattern = new RegExp(`\\b${escapeRegex(key)}\\b`, 'i');
      if (pattern.test(text)) {
        return pick;
      }
    }
  }
  return null;
}
